/**
 * S7 · FAULTS — did something go wrong, is it still wrong, what is my
 * evidence.
 *
 * Faults are EVIDENCE-RESOLVED, never click-resolved: one opens when its
 * evidence appears and closes when contradicting evidence appears. Muting is
 * client-local and expiring (D9) — there is no server-side acknowledgement,
 * because the write contract stays exactly one table.
 *
 * Ranking is money-at-risk × ambiguity, computed SERVER-SIDE, so a −$61
 * ambiguous close outranks a degraded poller. The client renders the order
 * it was given and never re-sorts. FOLDING IS NOT SORTING: adjacent
 * instances of one class that share their evidence strings verbatim are
 * drawn under one header so the shared forensic block is stated once. An
 * instance whose evidence differs from its neighbour's opens a new group,
 * and the server's order survives byte-for-byte.
 *
 * The ranking is carried by SIZE, not hue: rank 0's money-at-risk is the
 * largest figure on the surface. A page of 26 identical red paragraphs
 * teaches the reader to stop reading red.
 *
 * The NOT-EVALUABLE count is how a board proves it is clear BECAUSE IT
 * CHECKED rather than because it had nothing to read; the blind-spot
 * register is the honest complement, and it ends with the sentence that this
 * whole surface exists to earn. Both sit ABOVE the ranked list — nothing the
 * reader needs may live below a bounded scroller — but the register is a
 * DISCLOSURE carrying its own count, because five lines of English between
 * the figures and the fault is five lines the reader learns to skip.
 *
 * THE BRACKET filters self-resolved and closed. It NEVER filters OPEN: a fault
 * that opened before the reader's range is still money at risk right now, and
 * `open_outside_bracket` says how many arrived that way.
 *
 * COUPLING: `estimateSize` is measured against the type scale in tokens.css.
 * The list is `dynamic`, so a stale estimate is not a wrong render — it is a
 * measure pass on every row and a scrollbar that jumps.
 */

import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { api } from "../lib/api";
import { BracketBar } from "../lib/BracketBar";
import { refetchMsFor, scopeParam, useRange } from "../lib/bracket";
import { navigate } from "../lib/route";
import { ageSince, stamp } from "../lib/time";
import { VirtualList } from "../lib/VirtualList";

const MUTE_KEY = "regrine.mutes";
const MUTE_MS = 24 * 3600 * 1000;

function readMutes(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(MUTE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

interface Ranked {
  fault: any;
  /** the server's own index in the ranked list — never recomputed */
  rank: number;
}

interface FaultGroupRow {
  key: string;
  cls: string;
  /** the shared forensic block, rendered ONCE per group */
  known: string;
  not_known: string;
  engine_did: string;
  resolves_when: string;
  ledger_effect: string;
  money: number | null;
  items: Ranked[];
}

/** Adjacency-only folding. Never a re-sort, never a reorder: a group ends
 *  the moment the next row's class OR any evidence string differs. */
function groupAdjacent(open: any[]): FaultGroupRow[] {
  const out: FaultGroupRow[] = [];
  const ev = (f: any) => [f.known ?? "—", f.not_known ?? "—", f.engine_did ?? "—", f.resolves_when ?? "—", f.ledger_effect ?? "—"];
  open.forEach((f, i) => {
    const cur = out[out.length - 1];
    const [known, not_known, engine_did, resolves_when, ledger_effect] = ev(f) as [string, string, string, string, string];
    const same =
      cur &&
      cur.cls === f.class &&
      cur.known === known &&
      cur.not_known === not_known &&
      cur.engine_did === engine_did &&
      cur.resolves_when === resolves_when &&
      cur.ledger_effect === ledger_effect;
    const money = f.money_at_risk === null || f.money_at_risk === undefined ? null : Math.abs(f.money_at_risk);
    if (same && cur) {
      cur.items.push({ fault: f, rank: i });
      if (money !== null) cur.money = (cur.money ?? 0) + money;
      return;
    }
    out.push({
      key: `${f.class}:${f.fault_id}`,
      cls: f.class,
      known,
      not_known,
      engine_did,
      resolves_when,
      ledger_effect,
      money,
      items: [{ fault: f, rank: i }],
    });
  });
  return out;
}

export function Faults() {
  const range = useRange();
  // The bracket filters SELF-RESOLVED and CLOSED. It never filters OPEN: a
  // money fault that opened before the reader's range is still money at risk
  // right now, and a range that could hide it would be a range that lies.
  const q = useQuery({
    queryKey: ["faults", range.since, range.until],
    queryFn: () =>
      api.faults({ limit: 200, since: range.since, until: range.until, scope_label: scopeParam(range) }),
    refetchInterval: refetchMsFor(range),
  });
  const [mutes, setMutes] = useState<Record<string, number>>(readMutes);

  // Client-local and EXPIRING. Acking on the phone does not ack on the
  // desktop; that cost is accepted and stated, because the alternative is a
  // second write surface.
  const mute = useCallback((id: string) => {
    const next = { ...readMutes(), [id]: Date.now() + MUTE_MS };
    try {
      localStorage.setItem(MUTE_KEY, JSON.stringify(next));
    } catch {
      /* private mode: the mute simply does not persist */
    }
    setMutes(next);
  }, []);

  const now = Date.now();
  const open = (q.data?.open ?? []).filter((f: any) => (mutes[f.fault_id] ?? 0) < now);
  const muted = (q.data?.open ?? []).length - open.length;
  const selfResolved: any[] = q.data?.self_resolved ?? [];
  const outsideBracket: number = q.data?.open_outside_bracket ?? 0;
  const blindSpots: string[] = q.data?.blind_spots ?? [];
  const groups = useMemo(() => groupAdjacent(open), [open]);

  return (
    <section className="surface">
      <header className="surface__head">
        <h1 className="surface__title">Faults</h1>
        <div className="surface__meta mono">
          {open.length} open{muted > 0 ? ` · ${muted} muted` : ""} · {selfResolved.length} self-resolved
        </div>
      </header>

      <BracketBar />

      <div className="surface__body">
        {outsideBracket > 0 && (
          <span
            className="chip"
            title="open faults are shown whatever the bracket — a fault that opened earlier is still money at risk now"
          >
            {outsideBracket} open from before this range
          </span>
        )}

        {open.length === 0 &&
          (q.data?.assertion === "no faults in window" ? (
            <p className="note">{q.data.assertion}</p>
          ) : (
            /* "No faults in window" is only rendered when the pipeline that
               would have reported one is FRESH. Otherwise: cannot assert. */
            <div className="callout callout--stale">{q.data?.assertion ?? "alert sources STALE — cannot assert"}</div>
          ))}

        {q.data?.invariants && (
          <section className="band">
            <div className="band__title">INVARIANTS EVALUATED</div>
            <div className="figs">
              <div className="fig">
                <span className="fig__k">checks</span>
                <span className="fig__v mono num">{q.data.invariants.checks}</span>
              </div>
              <div className="fig">
                <span className="fig__k">objects</span>
                <span className="fig__v mono num">{q.data.invariants.objects}</span>
              </div>
              <div className="fig">
                <span className="fig__k">violations</span>
                <span className="fig__v mono num">{q.data.invariants.violations}</span>
              </div>
              <div className="fig">
                <span className="fig__k">not evaluable</span>
                <span className="fig__v">
                  <span className="chip" title="a clear board proves it CHECKED, not that it had nothing to read">
                    {q.data.invariants.not_evaluable} NOT EVALUABLE
                  </span>
                </span>
              </div>
            </div>
            <p className="note">
              run <span className="mono">{stamp(q.data.invariants.run_at)}</span>
            </p>
          </section>
        )}

        {selfResolved.length > 0 && (
          <section className="band">
            <div className="band__title">SELF-RESOLVED CLASSES</div>
            <div className="figs">
              {selfResolved.map((s: any) => (
                <div key={s.class} className="fig">
                  <span className="fig__k mono">{s.class}</span>
                  <span className="fig__v mono num">×{s.n}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* The COUNT stays visible; the register itself is one click away.
            Five items of English above the ranked list is five items the
            reader scrolls past on the way to the fault. */}
        <details className="disclose">
          <summary>blind spots ({blindSpots.length})</summary>
          <div className="band__title">NOT DETECTABLE FROM JOURNALS TODAY</div>
          <ul className="blindspots">
            {blindSpots.map((b: string) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          <p className="note">{q.data?.footer ?? '"no faults" is not proof of no faults.'}</p>
        </details>

        {/* LAST on the surface, deliberately: this is the one bounded
            scroller, and nothing the reader needs sits under it. */}
        <VirtualList
          rows={groups}
          ariaLabel="open faults"
          className="vlist vlist--faults"
          estimateSize={210}
          dynamic
          rowKey={(g: FaultGroupRow) => g.key}
          renderRow={(g: FaultGroupRow, i: number) => <FaultGroup group={g} rank={i} onMute={mute} />}
          emptyLabel={null}
        />
      </div>
    </section>
  );
}

function FaultGroup({ group, rank, onMute }: { group: FaultGroupRow; rank: number; onMute: (id: string) => void }) {
  const n = group.items.length;
  return (
    <div className="faultgroup" data-rank={rank}>
      <div className="faultgroup__head">
        <span className="verdict">{group.cls}</span>
        {n > 1 && <span className="chip">×{n}</span>}
        {group.money !== null && (
          <span className="fault__money mono num" title={n > 1 ? `money at risk, summed over ${n} instances` : "money at risk"}>
            ${group.money.toFixed(2)} at risk
          </span>
        )}
      </div>

      {/* KNOWN / NOT KNOWN as literal sub-headings — the forensic voice,
          stated ONCE for every instance that shares it verbatim. */}
      <div className="faultgroup__shared">
        <dl className="kvs">
          <dt className="kv__k">KNOWN</dt>
          <dd className="kv__v">{group.known}</dd>
          <dt className="kv__k">NOT KNOWN</dt>
          <dd className="kv__v">{group.not_known}</dd>
          <dt className="kv__k">ENGINE DID</dt>
          <dd className="kv__v">{group.engine_did}</dd>
          <dt className="kv__k">RESOLVES</dt>
          <dd className="kv__v">{group.resolves_when}</dd>
        </dl>
        <p className="note">LEDGER {group.ledger_effect}</p>
      </div>

      <div className="faultgroup__list">
        {group.items.map(({ fault, rank: r }) => (
          <Fault key={fault.fault_id} fault={fault} rank={r} showMoney={n > 1} onMute={() => onMute(fault.fault_id)} />
        ))}
      </div>
    </div>
  );
}

function Fault({
  fault,
  rank,
  showMoney,
  onMute,
}: {
  fault: any;
  rank: number;
  showMoney: boolean;
  onMute: () => void;
}) {
  const money = fault.money_at_risk === null || fault.money_at_risk === undefined ? null : Math.abs(fault.money_at_risk);
  return (
    <div className="fault" data-severity={fault.severity} data-rank={rank}>
      <div className="fault__head">
        <span className="chip">
          OPEN {ageSince(fault.opened_at)} · {fault.severity}
        </span>
        {showMoney && money !== null && <span className="fault__money mono num">${money.toFixed(2)}</span>}
      </div>
      <div className="row-actions">
        {fault.anchor_kind === "case" && (
          <button className="linkish" onClick={() => navigate({ name: "case", postId: fault.anchor_id, band: "outcome" })}>
            case
          </button>
        )}
        {fault.anchor_kind === "order" && (
          <button className="linkish" onClick={() => navigate({ name: "order", tradeId: fault.anchor_id })}>
            order
          </button>
        )}
        <button
          onClick={onMute}
          title="Hides it in this browser for 24 hours. The fault stays open, nobody else stops seeing it, and it comes back on its own — only contradicting evidence closes a fault."
        >
          mute 24h
        </button>
      </div>
    </div>
  );
}
